import React, { useState, useEffect } from 'react';
import { facilitiesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Building2,
  MapPin,
  Phone,
  Clock,
  Star,
  Search,
  Filter,
  Loader2,
  AlertCircle,
  Ambulance,
  ChevronRight,
  Stethoscope,
  Pill,
  FlaskConical,
  Baby,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const FACILITY_ICONS = {
  hospital: Building2,
  clinic: Stethoscope,
  pharmacy: Pill,
  laboratory: FlaskConical,
  imaging_center: FlaskConical,
  maternity: Baby,
  dental_clinic: Stethoscope
};

const FACILITY_COLORS = {
  hospital: 'bg-red-100 text-red-700 border-red-200',
  clinic: 'bg-blue-100 text-blue-700 border-blue-200',
  pharmacy: 'bg-green-100 text-green-700 border-green-200',
  laboratory: 'bg-purple-100 text-purple-700 border-purple-200',
  imaging_center: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  maternity: 'bg-pink-100 text-pink-700 border-pink-200',
  dental_clinic: 'bg-cyan-100 text-cyan-700 border-cyan-200'
};

const FACILITY_LABELS = {
  hospital: 'Hôpital',
  clinic: 'Clinique',
  pharmacy: 'Pharmacie',
  laboratory: 'Laboratoire',
  imaging_center: "Centre d'imagerie",
  maternity: 'Maternité',
  dental_clinic: 'Cabinet dentaire'
};

const Facilities = () => {
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [facilityType, setFacilityType] = useState('');
  const [emergencyOnly, setEmergencyOnly] = useState(false);
  const [cities, setCities] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadFacilities();
    loadCities();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadFacilities();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, city, facilityType, emergencyOnly]);

  const loadFacilities = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (city) params.city = city;
      if (facilityType) params.facility_type = facilityType;
      if (emergencyOnly) params.emergency_24h = true;
      
      const response = await facilitiesAPI.getAll(params);
      setFacilities(response.data.facilities || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading facilities:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const response = await facilitiesAPI.getCities();
      setCities(response.data.cities || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setCity('');
    setFacilityType('');
    setEmergencyOnly(false);
  };

  const hasActiveFilters = search || city || facilityType || emergencyOnly;

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6 w-full max-w-full overflow-x-hidden" data-testid="facilities-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Établissements de santé
        </h1>
        <p className="text-slate-500 mt-1">
          Trouvez hôpitaux, cliniques, pharmacies et laboratoires près de chez vous
        </p>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou service..."
              className="pl-10 rounded-xl"
              data-testid="search-input"
            />
          </div>
          
          {/* City Filter */}
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger className="w-full md:w-48 rounded-xl" data-testid="city-filter">
              <SelectValue placeholder="Toutes les villes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_cities">Toutes les villes</SelectItem>
              {cities.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Type Filter */}
          <Select value={facilityType} onValueChange={setFacilityType}>
            <SelectTrigger className="w-full md:w-48 rounded-xl" data-testid="type-filter">
              <SelectValue placeholder="Tous les types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_types">Tous les types</SelectItem>
              {Object.entries(FACILITY_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Emergency Toggle */}
          <Button
            variant={emergencyOnly ? 'default' : 'outline'}
            onClick={() => setEmergencyOnly(!emergencyOnly)}
            className={`rounded-xl ${emergencyOnly ? 'bg-red-500 hover:bg-red-600' : ''}`}
            data-testid="emergency-filter"
          >
            <Ambulance className="w-4 h-4 mr-2" />
            Urgences 24h
          </Button>
        </div>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t">
            <span className="text-sm text-slate-500">Filtres actifs:</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-red-500 hover:text-red-700"
            >
              <X className="w-4 h-4 mr-1" />
              Effacer tout
            </Button>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">
          {total} établissement{total > 1 ? 's' : ''} trouvé{total > 1 ? 's' : ''}
        </p>
      </div>

      {/* Facilities List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#E53935] animate-spin" />
        </div>
      ) : facilities.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="text-center py-12">
            <Building2 className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Aucun établissement trouvé</h3>
            <p className="text-slate-500 mt-2">
              Essayez de modifier vos critères de recherche
            </p>
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="mt-4"
              >
                Effacer les filtres
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {facilities.map((facility) => {
            const FacilityIcon = FACILITY_ICONS[facility.facility_type] || Building2;
            const colorClass = FACILITY_COLORS[facility.facility_type] || FACILITY_COLORS.clinic;
            
            return (
              <Card 
                key={facility.id}
                className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                onClick={() => navigate(`/facilities/${facility.id}`)}
                data-testid={`facility-${facility.id}`}
              >
                <CardContent className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClass}`}>
                        <FacilityIcon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 group-hover:text-[#1976D2] transition-colors line-clamp-1">
                          {facility.name}
                        </h3>
                        <Badge className={`text-xs ${colorClass}`}>
                          {FACILITY_LABELS[facility.facility_type]}
                        </Badge>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#1976D2] transition-colors" />
                  </div>

                  {/* Info */}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{facility.address}, {facility.city}</span>
                    </div>
                    
                    {facility.phone && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>{facility.phone}</span>
                      </div>
                    )}

                    {facility.opening_hours && typeof facility.opening_hours === 'string' && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{facility.opening_hours}</span>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    {/* Rating */}
                    <div className="flex items-center gap-1">
                      {facility.avg_rating ? (
                        <>
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                          <span className="font-medium text-slate-700">{facility.avg_rating}</span>
                          <span className="text-slate-400 text-xs">({facility.review_count})</span>
                        </>
                      ) : (
                        <span className="text-slate-400 text-xs">Pas encore d'avis</span>
                      )}
                    </div>

                    {/* Badges */}
                    <div className="flex items-center gap-1">
                      {facility.emergency_24h && (
                        <Badge className="bg-red-100 text-red-700 text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          24h
                        </Badge>
                      )}
                      {facility.has_ambulance && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">
                          <Ambulance className="w-3 h-3" />
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Facilities;
